import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { getWheel } from '../game/data.js';
import { VehicleInteriorEditor } from './vehicleInteriorEditor.js';
import { CatalogEquipmentPicker } from './catalogWheelPicker.js';
import { VEHICLE_EQUIPMENT_SLOTS, publishedEquipmentOptions, initialVehicleEquipment } from './vehicleEquipment.js';
import {
  VehicleAssetRig,
  createVehicleProfile,
  sanitizeVehicleProfile,
  VEHICLE_PROFILE_SCHEMA,
} from './vehicleProfile.js';

const $ = (root, selector) => root.querySelector(selector);
const $$ = (root, selector) => [...root.querySelectorAll(selector)];

function readPath(object, path) {
  return path.split('.').reduce((value, key) => value?.[key], object);
}

function writePath(object, path, value) {
  const keys = path.split('.');
  const finalKey = keys.pop();
  const target = keys.reduce((value, key) => value[key], object);
  target[finalKey] = value;
}

function cleanProfileName(value) {
  return String(value || 'vehiculo-personalizado')
    .replace(/\.[^.]+$/, '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'vehiculo-personalizado';
}

function sceneStats(object) {
  let meshes = 0;
  let triangles = 0;
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    meshes += 1;
    const geometry = child.geometry;
    triangles += geometry.index
      ? geometry.index.count / 3
      : (geometry.attributes.position?.count || 0) / 3;
  });
  return { meshes, triangles: Math.round(triangles) };
}

function formatTriangles(value) {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k tris` : `${value} tris`;
}

function disposeLoadedScene(object) {
  object?.traverse((child) => {
    if (!child.isMesh) return;
    child.geometry?.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => material.dispose?.());
  });
}

export function createDefaultCalibrationWheel() {
  const design = getWheel('chrome_classic');
  const radius = .92;
  const width = .44;
  const root = new THREE.Group();
  root.name = 'chrome_classic_calibration_wheel';
  const rubber = new THREE.MeshStandardMaterial({ color:0x242532, roughness:.82, metalness:.02 });
  const metal = new THREE.MeshStandardMaterial({ color:design.color, roughness:.3, metalness:.72 });
  const accent = new THREE.MeshStandardMaterial({ color:design.accent, roughness:.28, metalness:.62 });
  const brakeMaterial = new THREE.MeshStandardMaterial({ color:0x343943, roughness:.34, metalness:.75 });
  const tireProfile = [
    new THREE.Vector2(radius * design.inner, -width * .5),
    new THREE.Vector2(radius * .82, -width * .5),
    new THREE.Vector2(radius * .97, -width * design.tireRoundness),
    new THREE.Vector2(radius, width * design.tireRoundness),
    new THREE.Vector2(radius * .88, width * .5),
    new THREE.Vector2(radius * design.inner, width * .5),
  ];
  const tire = new THREE.Mesh(new THREE.LatheGeometry(tireProfile, 24), rubber);
  tire.rotation.z = Math.PI / 2;
  const brakeDisc = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * .55, radius * .55, width * .12, 20),
    brakeMaterial,
  );
  brakeDisc.rotation.z = Math.PI / 2;
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * design.hub, radius * design.hub, width * 1.12, 16),
    accent,
  );
  hub.rotation.z = Math.PI / 2;
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(radius * design.rim, radius * .055, 8, 24),
    metal,
  );
  rim.rotation.y = Math.PI / 2;
  rim.position.x = width * .54;
  const accentRing = new THREE.Mesh(
    new THREE.TorusGeometry(radius * .72, radius * .026, 7, 28),
    accent,
  );
  accentRing.rotation.y = Math.PI / 2;
  accentRing.position.x = width * .555;
  const centerCap = new THREE.Mesh(new THREE.SphereGeometry(radius * .14, 14, 9), accent);
  centerCap.scale.x = .36;
  centerCap.position.x = width * .6;
  root.add(tire, brakeDisc, hub, rim, accentRing, centerCap);
  for (let index = 0; index < design.spokes; index += 1) {
    const geometry = new RoundedBoxGeometry(
      width * design.spokeWidth,
      radius * design.spokeHeight,
      radius * design.spokeLength,
      2,
      radius * .025,
    );
    geometry.translate(width * .53, 0, -radius * design.spokeLength * .5);
    geometry.rotateX(index / design.spokes * Math.PI * 2);
    root.add(new THREE.Mesh(geometry, metal));
  }
  return root;
}

export class VehicleCalibrator {
  constructor(screen, callbacks = {}) {
    this.screen = screen;
    this.callbacks = callbacks;
    this.canvas = $(screen, '#calibrator-canvas');
    this.status = $(screen, '#calibrator-status');
    this.profileName = $(screen, '#calibrator-profile-name');
    this.profileNameMessage = $(screen, '#calibrator-profile-name-message');
    this.bodyInput = $(screen, '#calibrator-body-file');
    this.wheelInput = $(screen, '#calibrator-wheel-file');
    this.profileInput = $(screen, '#calibrator-profile-file');
    this.bodyFileLabel = $(screen, '#calibrator-body-name');
    this.wheelFileLabel = $(screen, '#calibrator-wheel-name');
    this.bodyInfo = $(screen, '#calibrator-body-info');
    this.wheelInfo = $(screen, '#calibrator-wheel-info');
    this.saveButton = $(screen, '#calibrator-save');
    this.defaultWheelSelect=$(screen,'#calibrator-default-wheel');
    this.defaultEngineSoundSelect=$(screen,'#calibrator-default-engine-sound');
    this.equipmentPickers = new Map(VEHICLE_EQUIPMENT_SLOTS.map((slot) => [
      slot.field, new CatalogEquipmentPicker($(screen, `#${slot.selectId}`), slot),
    ]));
    this.wheelPicker = this.equipmentPickers.get('defaultWheelId');
    this.comparisonSelect=$(screen,'#calibrator-comparison-vehicle');
    this.comparisonNote=$(screen,'#calibrator-comparison-note');
    this.floorLimitInput = $(screen, '#calibrator-floor-limit');
    this.heightCard = $(screen, '#calibrator-height-reference');
    this.heightValue = $(screen, '#calibrator-height-value');
    this.heightMeshValue = $(screen, '#calibrator-height-mesh-value');
    this.heightColliderValue = $(screen, '#calibrator-height-collider-value');
    this.heightComparison = $(screen, '#calibrator-height-comparison');
    this.active = false;
    this.hasBody = false;
    this.hasWheel = false;
    this.assetFiles = { body: null, wheel: null };
    this.comparisonMode='side';
    this.comparisonOptionsLoaded=false;
    this.comparisonLoadToken=0;
    this.comparisonItem=null;
    this.comparisonRig=null;
    this.profileNameIsAvailable=true;
    this.wheelSelectionToken=0;

    this.profile = createVehicleProfile();
    this.rig = new VehicleAssetRig(this.profile);
    this.initScene();
    this.installDefaultCalibrationWheel();
    this.interiorEditor = new VehicleInteriorEditor(this);
    this.bindInterface();
    this.syncControls();
    this.syncComparisonControls();
    this.applyProfile();
    this.animate();
  }

  initScene() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#17172a');
    this.scene.fog = new THREE.Fog('#17172a', 16, 48);
    const environmentGenerator = new THREE.PMREMGenerator(this.renderer);
    this.environmentTexture = environmentGenerator.fromScene(new RoomEnvironment(), .04).texture;
    this.scene.environment = this.environmentTexture;
    environmentGenerator.dispose();
    this.camera = new THREE.PerspectiveCamera(42, 1, .03, 120);
    this.camera.position.set(6.2, 3.6, -7.2);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = .075;
    this.controls.target.set(0, .72, 0);
    this.controls.minDistance = 2.2;
    this.controls.maxDistance = 40;
    this.controls.maxPolarAngle = Math.PI * .49;

    this.scene.add(new THREE.HemisphereLight(0xe8f3ff, 0x25213a, 2.0));
    const key = new THREE.DirectionalLight(0xffedd3, 4.2);
    key.position.set(-5, 9, -6);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.left = -10;
    key.shadow.camera.right = 10;
    key.shadow.camera.top = 10;
    key.shadow.camera.bottom = -10;
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0x63cfff, 1.8);
    fill.position.set(6, 4, 2);
    this.scene.add(fill);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(30, 30),
      new THREE.ShadowMaterial({ color: 0x090813, opacity: .36 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -.006;
    floor.receiveShadow = true;
    this.scene.add(floor);
    const grid = new THREE.GridHelper(20, 40, 0x6d58dc, 0x37334d);
    grid.material.transparent = true;
    grid.material.opacity = .45;
    this.scene.add(grid);

    const forward = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, .012, 0),
      4.5,
      0x3de4ee,
      .26,
      .16,
    );
    forward.name = 'vehicle_forward_-Z';
    this.scene.add(forward);

    this.guideGroup = new THREE.Group();
    this.guideGroup.name = 'calibration_guides';
    this.guideLines = {};
    this.guideRings = {};
    for (const [axle, color] of [['front', 0xffd83d], ['rear', 0x39d9ee]]) {
      const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: .92, depthTest: false });
      const line = new THREE.Line(new THREE.BufferGeometry(), material);
      line.renderOrder = 20;
      this.guideGroup.add(line);
      this.guideLines[axle] = line;
      for (const side of ['L', 'R']) {
        const points = [];
        for (let index = 0; index < 64; index += 1) {
          const angle = index / 64 * Math.PI * 2;
          points.push(new THREE.Vector3(0, Math.cos(angle), Math.sin(angle)));
        }
        const ring = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(points), material.clone());
        ring.renderOrder = 21;
        this.guideGroup.add(ring);
        this.guideRings[`${axle}${side}`] = ring;
      }
    }
    this.colliderGuide = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
      new THREE.LineBasicMaterial({ color: 0xff5e88, transparent: true, opacity: .72, depthTest: false }),
    );
    this.colliderGuide.name = 'gameplay_collider_preview';
    this.colliderGuide.renderOrder = 22;
    this.guideGroup.add(this.colliderGuide);
    this.weaponSocketGuide=new THREE.ArrowHelper(
      new THREE.Vector3(0,0,-1),
      new THREE.Vector3(),
      .62,
      0xffd83d,
      .18,
      .11,
    );
    this.weaponSocketGuide.name='weapon_socket_minus_z';
    this.nitroSocketGuide=new THREE.ArrowHelper(
      new THREE.Vector3(0,0,1),
      new THREE.Vector3(),
      .62,
      0x39d9ee,
      .18,
      .11,
    );
    this.nitroSocketGuide.name='nitro_socket_plus_z';
    this.guideGroup.add(this.weaponSocketGuide,this.nitroSocketGuide);
    this.heightRuler = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color:0x39d9ee, transparent:true, opacity:.9, depthTest:false }),
    );
    this.heightRuler.name = 'vehicle_height_ruler';
    this.heightRuler.renderOrder = 23;
    this.heightRoofMarker = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color:0xffd83d, transparent:true, opacity:.95, depthTest:false }),
    );
    this.heightRoofMarker.name = 'vehicle_roof_height';
    this.heightRoofMarker.renderOrder = 24;
    this.heightColliderMarker = new THREE.Line(
      new THREE.BufferGeometry(),
      new THREE.LineBasicMaterial({ color:0xff5e88, transparent:true, opacity:.9, depthTest:false }),
    );
    this.heightColliderMarker.name = 'vehicle_collider_height';
    this.heightColliderMarker.renderOrder = 24;
    this.guideGroup.add(this.heightRuler,this.heightRoofMarker,this.heightColliderMarker);
    this.comparisonMount=new THREE.Group();
    this.comparisonMount.name='catalog_vehicle_comparison';
    this.comparisonMount.visible=false;
    this.comparisonColliderGuide=new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1,1,1)),
      new THREE.LineBasicMaterial({
        color:0x39d9ee,
        transparent:true,
        opacity:.62,
        depthTest:false,
      }),
    );
    this.comparisonColliderGuide.name='comparison_gameplay_collider';
    this.comparisonColliderGuide.renderOrder=25;
    this.comparisonMount.add(this.comparisonColliderGuide);
    this.scene.add(this.rig.root, this.guideGroup, this.comparisonMount);
    this.updateGuides();

    this.loader = new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
    this.loader.setDRACOLoader(this.dracoLoader);
  }

  bindInterface() {
    $(this.screen, '#calibrator-close').addEventListener('click', () => this.close());
    this.bodyInput.addEventListener('change', () => this.loadGlbFile(this.bodyInput.files[0], 'body'));
    this.wheelInput.addEventListener('change', () => this.loadGlbFile(this.wheelInput.files[0], 'wheel'));
    this.profileInput.addEventListener('change', () => this.loadProfileFile(this.profileInput.files[0]));
    this.saveButton.addEventListener('click', () => this.saveProfile());
    for(const [field,picker] of this.equipmentPickers){
      picker.select.addEventListener('change',()=>this.selectCatalogEquipment(field,picker.select.value));
    }
    this.comparisonSelect.addEventListener('change',()=>{
      this.loadComparisonVehicle(this.comparisonSelect.value);
    });
    $$(this.screen,'[data-comparison-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setComparisonMode(button.dataset.comparisonMode));
    });
    $(this.screen, '#calibrator-reset').addEventListener('click', () => this.resetFit());
    $(this.screen, '#calibrator-frame').addEventListener('click', () => this.frameVehicle());

    this.profileName.addEventListener('input', () => {
      this.profile.name = cleanProfileName(this.profileName.value);
      this.updateProfileNameValidation();
    });
    this.profileName.addEventListener('blur',()=>this.updateProfileNameValidation({announce:true}));

    $$(this.screen, '[data-profile]').forEach((control) => {
      const sectionName = control.closest('details')?.querySelector('summary')?.textContent?.trim() || 'Vehículo';
      const fieldName = control.closest('label')?.querySelector('span')?.childNodes?.[0]?.textContent?.trim() || control.dataset.profile;
      control.setAttribute('aria-label', `${sectionName} · ${fieldName}`);
      const commitControl = () => {
        const value = control.type === 'checkbox' ? control.checked : Number(control.value);
        writePath(this.profile, control.dataset.profile, value);
        this.applyProfile();
        this.updateControlOutput(control);
      };
      control.addEventListener('input', commitControl);
      control.addEventListener('change', commitControl);
    });

    $$(this.screen, '[data-view]').forEach((button) => {
      button.addEventListener('click', () => this.setView(button.dataset.view));
    });
    $(this.screen, '#calibrator-show-body').addEventListener('change', (event) => {
      this.rig.bodyMount.visible = event.target.checked;
      this.interiorEditor?.updateGuide();
    });
    $(this.screen, '#calibrator-show-wheels').addEventListener('change', (event) => {
      this.rig.wheelGroup.visible = event.target.checked;
    });
    $(this.screen, '#calibrator-show-guides').addEventListener('change', (event) => {
      this.guideGroup.visible = event.target.checked;
      this.heightCard.hidden = !event.target.checked || !this.hasBody;
    });
    this.floorLimitInput.addEventListener('change', () => {
      this.applyProfile();
      this.setStatus(
        this.floorLimitInput.checked
          ? 'Límite de piso activado: las piezas no pueden atravesar la cuadrícula.'
          : 'Límite de piso desactivado: ahora podés mover piezas debajo de la cuadrícula.',
        'ready',
      );
    });
    $(this.screen, '#calibrator-body-opacity').addEventListener('input', (event) => {
      this.setBodyOpacity(Number(event.target.value));
      $(this.screen, '#calibrator-body-opacity-value').textContent = `${Math.round(Number(event.target.value) * 100)}%`;
    });

    window.addEventListener('resize', () => this.resize());
    window.addEventListener('keydown', (event) => {
      if (this.active && event.code === 'Escape') this.close();
    });
  }

  open() {
    this.active = true;
    this.screen.classList.remove('hidden');
    this.callbacks.onOpen?.();
    this.refreshComparisonOptions();
    this.resize();
    this.setStatus('Cargá una carrocería GLB. La rueda GLB es opcional.', 'ready');
    requestAnimationFrame(() => this.resize());
  }

  close() {
    this.active = false;
    this.screen.classList.add('hidden');
    this.callbacks.onClose?.();
  }

  async loadGlbFile(file, type) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.glb')) {
      this.setStatus('Este calibrador acepta archivos GLB. Exportá el modelo nuevamente como .glb.', 'error');
      return;
    }
    if (file.size > 250 * 1024 * 1024) {
      this.setStatus('El GLB supera 250 MB. Conviene optimizarlo antes de calibrar.', 'error');
      return;
    }

    if(type==='wheel')this.wheelSelectionToken++;
    this.setStatus(`Analizando ${file.name}…`, 'loading');
    try {
      const data = await file.arrayBuffer();
      const gltf = await this.loader.parseAsync(data, '');
      const scene = gltf.scene || gltf.scenes?.[0];
      if (!scene) throw new Error('El GLB no contiene una escena válida.');
      const stats = sceneStats(scene);
      this.rig.profile = this.profile;
      if (type === 'body') {
        this.rig.setBody(scene, file.name);
        this.hasBody = true;
        this.assetFiles.body = file;
        this.bodyFileLabel.textContent = file.name;
        this.bodyInfo.textContent = `${stats.meshes} mallas · ${formatTriangles(stats.triangles)}`;
        if (this.profile.name === 'vehiculo-personalizado') {
          this.profile.name = cleanProfileName(file.name);
          this.profileName.value = this.profile.name;
          this.updateProfileNameValidation({announce:true});
        }
        this.setBodyOpacity(Number($(this.screen, '#calibrator-body-opacity').value));
      } else {
        this.rig.setWheel(scene, file.name, { inheritBodyFinish:true });
        this.hasWheel = true;
        this.assetFiles.wheel = file;
        this.wheelFileLabel.textContent = file.name;
        this.wheelInfo.textContent = `${stats.meshes} mallas · ${formatTriangles(stats.triangles)}`;
      }
      this.applyProfile();
      this.updateSaveAvailability();
      this.setStatus(`${file.name} cargado correctamente.`, 'success');
      if(!this.profileNameIsAvailable){
        this.setStatus(this.profileNameValidationMessage,'error');
      }
      this.frameVehicle();
    } catch (error) {
      console.error(error);
      this.setStatus(`No se pudo cargar ${file.name}: ${error.message}`, 'error');
    } finally {
      if (type === 'body') this.bodyInput.value = '';
      else this.wheelInput.value = '';
    }
  }

  async loadProfileFile(file) {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      if (parsed.schema !== VEHICLE_PROFILE_SCHEMA) {
        throw new Error('El JSON no es un perfil de vehículo de The Turbo Crown.');
      }
      this.profile = sanitizeVehicleProfile(parsed);
      this.rig.setProfile(this.profile);
      this.syncControls();
      this.applyProfile();
      this.syncCatalogDefaults();
      this.setStatus('Perfil cargado. Volvé a seleccionar sus GLB si todavía no están abiertos.', 'success');
    } catch (error) {
      this.setStatus(`No se pudo abrir el perfil: ${error.message}`, 'error');
    } finally {
      this.profileInput.value = '';
    }
  }

  async loadVehicleRecord(record) {
    this.clearWorkspace(record?.profile?.name || record?.id || 'vehiculo-personalizado');
    if (record?.profile) {
      this.profile = sanitizeVehicleProfile(record.profile);
      this.rig.setProfile(this.profile);
      this.syncControls();
      this.applyProfile();
      this.syncCatalogDefaults();
    }
    if (record?.bodyFile) await this.loadGlbFile(record.bodyFile, 'body');
    if (record?.wheelFile) await this.loadGlbFile(record.wheelFile, 'wheel');
    else await this.selectDefaultWheel(this.profile.game.defaultWheelId);
    this.frameVehicle();
  }

  clearWorkspace(name = 'vehiculo-personalizado') {
    this.wheelSelectionToken++;
    this.scene.remove(this.rig.root);
    this.rig.dispose();
    this.profile = createVehicleProfile(cleanProfileName(name));
    this.rig = new VehicleAssetRig(this.profile);
    this.scene.add(this.rig.root);
    this.hasBody = false;
    this.assetFiles = { body: null, wheel: null };
    this.bodyFileLabel.textContent = 'Elegir archivo';
    this.bodyInfo.textContent = 'Todavía no cargada';
    this.installDefaultCalibrationWheel();
    this.syncCatalogDefaults();
    this.saveButton.disabled = true;
    this.syncControls();
    this.applyProfile();
    this.setView('front');
  }

  installDefaultCalibrationWheel() {
    this.rig.profile = this.profile;
    this.rig.setWheel(createDefaultCalibrationWheel(), '');
    this.profile.assets.wheelFile = '';
    this.hasWheel = true;
    this.wheelFileLabel.textContent = 'Cromo Clásico incluido';
    this.wheelInfo.textContent = 'Rueda procedural del juego · GLB opcional';
  }

  setCatalogDefaults(wheels=[],engineSounds=[]){
    this.setCatalogEquipment([...wheels,...engineSounds]);
  }

  setCatalogEquipment(items=[]){
    this.catalogEquipment=items;
    this.catalogWheels=publishedEquipmentOptions(items,'wheel');
    this.catalogEngineSounds=publishedEquipmentOptions(items,'engine_sound');
    for(const {field,type} of VEHICLE_EQUIPMENT_SLOTS){
      this.equipmentPickers.get(field).setItems(publishedEquipmentOptions(items,type));
    }
    this.syncCatalogDefaults();
  }

  async initializeCatalogEquipment(){
    const equipment=initialVehicleEquipment(this.catalogEquipment||[],this.profile.game);
    const wheelId=equipment.defaultWheelId;
    equipment.defaultWheelId=this.profile.game.defaultWheelId;
    Object.assign(this.profile.game,equipment);
    this.syncCatalogDefaults();
    if(this.catalogWheels?.some((item)=>item.id===wheelId))await this.selectDefaultWheel(wheelId);
  }

  selectCatalogEquipment(field,id){
    const picker=this.equipmentPickers.get(field);
    const item=picker?.items.find((option)=>option.id===id);
    if(!item){this.syncCatalogDefaults();return;}
    if(field==='defaultWheelId')return this.selectDefaultWheel(id);
    this.profile.game[field]=id;
    if(field==='defaultCannonId'&&!this.profile.game.defaultProjectileId){
      const projectileId=item.profile?.design?.defaultProjectileId;
      if(this.equipmentPickers.get('defaultProjectileId').items.some((option)=>option.id===projectileId)){
        this.profile.game.defaultProjectileId=projectileId;
      }
    }
    this.syncCatalogDefaults();
  }

  syncCatalogDefaults(){
    for(const [field,picker] of this.equipmentPickers)picker.sync(this.profile.game[field]);
  }

  syncComparisonControls(){
    $$(this.screen,'[data-comparison-mode]').forEach((button)=>{
      const selected=button.dataset.comparisonMode===this.comparisonMode;
      button.classList.toggle('selected',selected);
      button.setAttribute('aria-selected',String(selected));
      button.disabled=!this.comparisonRig;
    });
  }

  disposeComparisonRig(){
    if(!this.comparisonRig)return;
    this.comparisonMount.remove(this.comparisonRig.root);
    disposeLoadedScene(this.comparisonRig.root);
    this.comparisonRig.dispose();
    this.comparisonRig=null;
    this.comparisonItem=null;
  }

  clearComparison({resetSelect=true,invalidate=true}={}){
    if(invalidate)this.comparisonLoadToken+=1;
    this.disposeComparisonRig();
    this.comparisonMount.visible=false;
    if(resetSelect)this.comparisonSelect.value='';
    this.comparisonNote.textContent='Elegí otro auto del catálogo para comparar sus dimensiones visuales.';
    this.comparisonNote.dataset.state='ready';
    this.syncComparisonControls();
  }

  async refreshComparisonOptions(){
    const previousId=this.comparisonSelect.value;
    try{
      const vehicles=(await this.callbacks.onListComparisonVehicles?.()||[])
        .filter((item)=>item.type==='vehicle')
        .sort((first,second)=>(first.name||first.id).localeCompare(second.name||second.id,'es'));
      this.comparisonSelect.replaceChildren();
      const emptyOption=document.createElement('option');
      emptyOption.value='';
      emptyOption.textContent=vehicles.length?'Sin referencia':'No hay otro auto disponible';
      this.comparisonSelect.append(emptyOption);
      vehicles.forEach((item)=>{
        const option=document.createElement('option');
        option.value=item.id;
        option.textContent=item.name||item.id;
        this.comparisonSelect.append(option);
      });
      const selected=vehicles.some((item)=>item.id===previousId)?previousId:'';
      this.comparisonSelect.value=selected;
      this.comparisonSelect.disabled=!vehicles.length;
      this.comparisonOptionsLoaded=true;
      if(!selected&&this.comparisonRig)this.clearComparison({resetSelect:false});
      else if(!vehicles.length){
        this.comparisonNote.textContent='Guardá otro auto en el catálogo para usarlo como referencia.';
        this.comparisonNote.dataset.state='ready';
      }
      this.syncComparisonControls();
    }catch(error){
      console.error(error);
      this.clearComparison();
      this.comparisonSelect.replaceChildren();
      const option=document.createElement('option');
      option.value='';
      option.textContent='No se pudieron cargar los autos';
      this.comparisonSelect.append(option);
      this.comparisonSelect.disabled=true;
      this.comparisonNote.textContent=error.message||'No se pudo leer el catálogo de autos.';
      this.comparisonNote.dataset.state='error';
    }
  }

  applyComparisonAppearance(){
    if(!this.comparisonRig)return;
    const opacity=this.comparisonMode==='overlay'?.28:.64;
    this.comparisonRig.root.traverse((child)=>{
      if(!child.isMesh)return;
      child.castShadow=false;
      child.receiveShadow=false;
      const sourceMaterials=Array.isArray(child.material)?child.material:[child.material];
      const materials=sourceMaterials.filter(Boolean).map((source)=>{
        const existing=Boolean(source.userData.catalogComparison);
        const material=existing?source:source.clone();
        if(!existing){
          material.userData.catalogComparison=true;
          if(material.color)material.color.lerp(new THREE.Color(0x39d9ee),.28);
          if(material.emissive){
            material.emissive.set(0x123e4c);
            material.emissiveIntensity=.24;
          }
        }
        material.transparent=true;
        material.opacity=opacity;
        material.depthWrite=false;
        material.needsUpdate=true;
        return material;
      });
      child.material=Array.isArray(child.material)?materials:materials[0];
      child.renderOrder=6;
    });
    this.comparisonColliderGuide.material.opacity=this.comparisonMode==='overlay'?.78:.52;
  }

  updateComparisonNote(){
    if(!this.comparisonRig||!this.comparisonItem)return;
    const currentSize=new THREE.Box3().setFromObject(this.rig.root).getSize(new THREE.Vector3());
    const referenceSize=new THREE.Box3().setFromObject(this.comparisonRig.root).getSize(new THREE.Vector3());
    const format=(size)=>`${size.x.toFixed(2)} × ${size.y.toFixed(2)} × ${size.z.toFixed(2)}`;
    this.comparisonNote.textContent=`Visual actual ${format(currentSize)} · ${this.comparisonItem.name||this.comparisonItem.id} ${format(referenceSize)} · ancho × alto × largo`;
    this.comparisonNote.dataset.state='success';
  }

  updateComparisonPlacement(){
    if(!this.comparisonRig||!this.comparisonItem)return;
    this.comparisonMount.position.set(0,0,0);
    const collider=this.comparisonRig.profile.collider;
    this.comparisonColliderGuide.position.set(
      collider.offset.x,
      collider.offset.y,
      collider.offset.z,
    );
    this.comparisonColliderGuide.scale.set(
      collider.size.x,
      collider.size.y,
      collider.size.z,
    );
    this.comparisonMount.updateMatrixWorld(true);
    if(this.comparisonMode==='side'){
      const currentBounds=this.getProfileBounds(this.rig,this.profile);
      const comparisonBounds=this.getProfileBounds(this.comparisonRig,this.comparisonRig.profile);
      this.comparisonMount.position.x=currentBounds.max.x-comparisonBounds.min.x+.75;
    }
    this.comparisonMount.updateMatrixWorld(true);
    this.updateComparisonNote();
  }

  setComparisonMode(mode){
    if(mode!=='side'&&mode!=='overlay')return;
    this.comparisonMode=mode;
    this.syncComparisonControls();
    if(!this.comparisonRig)return;
    this.applyComparisonAppearance();
    this.updateComparisonPlacement();
    this.frameVehicle();
  }

  async loadComparisonVehicle(id){
    const loadToken=++this.comparisonLoadToken;
    if(!id){
      this.clearComparison({invalidate:false});
      this.frameVehicle();
      return;
    }
    this.comparisonSelect.disabled=true;
    this.comparisonNote.textContent='Cargando el auto de referencia…';
    this.comparisonNote.dataset.state='loading';
    try{
      const item=await this.callbacks.onLoadComparisonVehicle?.(id);
      if(loadToken!==this.comparisonLoadToken)return;
      if(!item?.bodyFile)throw new Error('El auto seleccionado no tiene una carrocería GLB disponible.');
      const gltf=await this.loader.parseAsync(await item.bodyFile.arrayBuffer(),'');
      const scene=gltf.scene||gltf.scenes?.[0];
      if(!scene)throw new Error('El GLB del auto de referencia no contiene una escena válida.');
      if(loadToken!==this.comparisonLoadToken){
        disposeLoadedScene(scene);
        return;
      }
      const rig=new VehicleAssetRig(item.profile);
      rig.setBody(scene,item.bodyFile.name);
      rig.setWheel(createDefaultCalibrationWheel(),'rueda-de-referencia');
      if(loadToken!==this.comparisonLoadToken){
        rig.dispose();
        return;
      }
      this.disposeComparisonRig();
      this.comparisonRig=rig;
      this.comparisonItem=item;
      this.comparisonMount.add(rig.root);
      this.comparisonMount.visible=true;
      this.applyComparisonAppearance();
      this.updateComparisonPlacement();
      this.syncComparisonControls();
      this.setStatus(`${item.name||item.id} cargado como referencia de tamaño.`,'success');
      this.frameVehicle();
    }catch(error){
      if(loadToken!==this.comparisonLoadToken)return;
      console.error(error);
      this.clearComparison({invalidate:false});
      this.comparisonNote.textContent=error.message||'No se pudo cargar el auto de referencia.';
      this.comparisonNote.dataset.state='error';
      this.setStatus(this.comparisonNote.textContent,'error');
    }finally{
      if(loadToken===this.comparisonLoadToken)this.comparisonSelect.disabled=false;
    }
  }

  async selectDefaultWheel(id){
    if(this.catalogWheels&&!this.catalogWheels.some((item)=>item.id===id)){
      this.syncCatalogDefaults();
      return;
    }
    const loadToken=++this.wheelSelectionToken;
    const profile=this.profile;
    try{
      const selection=await this.callbacks.onSelectDefaultWheel?.(id);
      let object=selection?.object||selection;
      const inheritBodyFinish=Boolean(selection?.inheritBodyFinish);
      if(selection?.assetFile){
        const gltf=await this.loader.parseAsync(await selection.assetFile.arrayBuffer(),'');
        object=gltf.scene||gltf.scenes?.[0];
        if(!object)throw new Error('La rueda GLB no contiene una escena válida.');
      }
      if(object){
        if(loadToken!==this.wheelSelectionToken||profile!==this.profile
          ||(this.catalogWheels&&!this.catalogWheels.some((item)=>item.id===id))){
          disposeLoadedScene(object);
          return;
        }
        this.profile.game.defaultWheelId=id;
        this.rig.setWheel(object,'',{inheritBodyFinish});
        this.profile.assets.wheelFile='';
        this.assetFiles.wheel=null;
        this.wheelFileLabel.textContent=this.catalogWheels?.find((item)=>item.id===id)?.name||id;
        this.wheelInfo.textContent='Rueda del catálogo · guardada como predeterminada';
        this.applyProfile();
      }
    }catch(error){
      if(loadToken===this.wheelSelectionToken&&profile===this.profile){
        this.setStatus(error.message||'No se pudo cargar la rueda del catálogo.','error');
      }
    }finally{
      if(loadToken===this.wheelSelectionToken)this.syncCatalogDefaults();
    }
  }

  applyProfile() {
    this.rig.profile = this.profile;
    const correctedPaths = this.enforceParametricFloorLimit();
    this.rig.applyProfile();
    if (this.enforceBodyFloorLimit()) {
      correctedPaths.add('body.offset.y');
      this.rig.applyProfile();
    }
    const collider = this.profile.collider;
    this.colliderGuide.position.set(collider.offset.x, collider.offset.y, collider.offset.z);
    this.colliderGuide.scale.set(collider.size.x, collider.size.y, collider.size.z);
    this.updateGuides();
    this.updateComparisonPlacement();
    correctedPaths.forEach((path) => this.syncProfileControl(path));
    this.interiorEditor?.updateGuide();
  }

  enforceParametricFloorLimit() {
    const corrected = new Set();
    if (!this.floorLimitInput.checked) return corrected;

    for (const axleName of ['front', 'rear']) {
      const axle = this.profile.wheels[axleName];
      if (axle.centerY < axle.radius) {
        axle.centerY = axle.radius;
        corrected.add(`wheels.${axleName}.centerY`);
      }
    }

    const collider = this.profile.collider;
    const minimumCenterY = collider.size.y * .5;
    if (collider.offset.y < minimumCenterY) {
      collider.offset.y = Math.ceil((minimumCenterY - 1e-8) * 100) / 100;
      corrected.add('collider.offset.y');
    }
    return corrected;
  }

  enforceBodyFloorLimit() {
    if (!this.floorLimitInput.checked || !this.rig.bodySource) return false;
    this.rig.bodyMount.updateWorldMatrix(true, true);
    // Interior fillers must not unexpectedly lift the entire vehicle while being edited.
    const bounds = new THREE.Box3().setFromObject(this.rig.bodySource);
    if (bounds.isEmpty() || bounds.min.y >= -1e-5) return false;
    const raisedHeight = this.profile.body.offset.y - bounds.min.y;
    this.profile.body.offset.y = Math.ceil((raisedHeight - 1e-8) * 100) / 100;
    return true;
  }

  syncProfileControl(path) {
    const control = this.screen.querySelector(`[data-profile="${path}"]`);
    if (!control) return;
    control.value = readPath(this.profile, path);
    this.updateControlOutput(control);
  }

  syncControls() {
    this.syncCatalogDefaults();
    this.interiorEditor?.sync();
    this.profileName.value = this.profile.name;
    this.updateProfileNameValidation();
    $$(this.screen, '[data-profile]').forEach((control) => {
      const value = readPath(this.profile, control.dataset.profile);
      if (control.type === 'checkbox') control.checked = Boolean(value);
      else control.value = value;
      this.updateControlOutput(control);
    });
  }

  updateControlOutput(control) {
    control.syncNumberControl?.();
    const output = this.screen.querySelector(`[data-output="${control.dataset.profile}"]`);
    if (!output) return;
    const value = Number(control.value);
    const precision = control.step && Number(control.step) < 1 ? 2 : 0;
    output.textContent = `${value.toFixed(precision)}${control.dataset.unit || ''}`;
  }

  updateGuides() {
    for (const axleName of ['front', 'rear']) {
      const axle = this.profile.wheels[axleName];
      const halfTrack = axle.track * .5;
      const line = this.guideLines[axleName];
      line.geometry.dispose();
      line.geometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-halfTrack, axle.centerY, axle.axleZ),
        new THREE.Vector3(halfTrack, axle.centerY, axle.axleZ),
      ]);
      for (const side of ['L', 'R']) {
        const ring = this.guideRings[`${axleName}${side}`];
        ring.position.set((side === 'L' ? -1 : 1) * halfTrack, axle.centerY, axle.axleZ);
        ring.scale.setScalar(axle.radius);
      }
    }
    this.weaponSocketGuide.position.set(
      this.profile.sockets.weapon.x,
      this.profile.sockets.weapon.y,
      this.profile.sockets.weapon.z,
    );
    this.nitroSocketGuide.position.set(
      this.profile.sockets.nitro.x,
      this.profile.sockets.nitro.y,
      this.profile.sockets.nitro.z,
    );
    this.updateHeightReference();
  }

  updateHeightReference() {
    const references=[this.heightRuler,this.heightRoofMarker,this.heightColliderMarker];
    if(!this.hasBody){
      references.forEach((reference)=>{reference.visible=false;});
      this.heightCard.hidden=true;
      this.heightValue.textContent='—';
      this.heightMeshValue.textContent='—';
      this.heightColliderValue.textContent=this.profile.collider.size.y.toFixed(2);
      this.heightComparison.textContent='Cargá una carrocería para medirla desde el piso.';
      return;
    }

    this.rig.root.updateWorldMatrix(true,true);
    const bounds=new THREE.Box3().setFromObject(this.rig.root);
    if(bounds.isEmpty()){
      references.forEach((reference)=>{reference.visible=false;});
      this.heightCard.hidden=true;
      return;
    }

    const meshHeight=bounds.max.y-bounds.min.y;
    const floorHeight=Math.max(0,bounds.max.y);
    const colliderTop=this.profile.collider.offset.y+this.profile.collider.size.y*.5;
    const rulerMaximum=Math.max(.5,Math.ceil(Math.max(floorHeight,colliderTop)/.25)*.25);
    const rulerX=bounds.min.x-.42;
    const rulerZ=bounds.min.z-.22;
    const rulerPoints=[
      new THREE.Vector3(rulerX,0,rulerZ),
      new THREE.Vector3(rulerX,rulerMaximum,rulerZ),
    ];
    const tickCount=Math.round(rulerMaximum/.25);
    for(let index=0;index<=tickCount;index+=1){
      const y=index*.25;
      const major=index%2===0;
      rulerPoints.push(
        new THREE.Vector3(rulerX-(major?.13:.075),y,rulerZ),
        new THREE.Vector3(rulerX+(major?.13:.075),y,rulerZ),
      );
    }
    this.heightRuler.geometry.dispose();
    this.heightRuler.geometry=new THREE.BufferGeometry().setFromPoints(rulerPoints);
    this.heightRoofMarker.geometry.dispose();
    this.heightRoofMarker.geometry=new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(rulerX-.13,floorHeight,rulerZ),
      new THREE.Vector3(bounds.min.x+.12,floorHeight,rulerZ),
    ]);
    this.heightColliderMarker.geometry.dispose();
    this.heightColliderMarker.geometry=new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(rulerX-.13,colliderTop,rulerZ),
      new THREE.Vector3(rulerX+.22,colliderTop,rulerZ),
    ]);
    references.forEach((reference)=>{reference.visible=true;});

    this.heightCard.hidden=!$(this.screen,'#calibrator-show-guides').checked;
    this.heightValue.textContent=floorHeight.toFixed(2);
    this.heightMeshValue.textContent=meshHeight.toFixed(2);
    this.heightColliderValue.textContent=this.profile.collider.size.y.toFixed(2);
    const difference=floorHeight-colliderTop;
    if(Math.abs(difference)<.03){
      this.heightComparison.textContent='El techo visual coincide con la parte superior del collider.';
      this.heightCard.dataset.state='aligned';
    }else if(difference>0){
      this.heightComparison.textContent=`La carrocería sobresale ${difference.toFixed(2)} u por encima del collider.`;
      this.heightCard.dataset.state='over';
    }else{
      this.heightComparison.textContent=`El collider sobresale ${Math.abs(difference).toFixed(2)} u por encima de la carrocería.`;
      this.heightCard.dataset.state='under';
    }
  }

  setBodyOpacity(opacity) {
    this.rig.bodyMount.traverse((child) => {
      if (!child.isMesh) return;
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.filter(Boolean).forEach((material) => {
        if (!material.userData.calibratorAppearance) {
          material.userData.calibratorAppearance = {
            opacity: material.opacity,
            transparent: material.transparent,
            depthWrite: material.depthWrite,
          };
        }
        const original = material.userData.calibratorAppearance;
        material.transparent = original.transparent || opacity < .995;
        material.opacity = original.opacity * opacity;
        material.depthWrite = original.depthWrite && opacity >= .995;
        material.needsUpdate = true;
      });
    });
  }

  getProfileBounds(rig,profile,offset=new THREE.Vector3()) {
    const box = new THREE.Box3().setFromObject(rig.root);
    const collider = profile.collider;
    const halfCollider = new THREE.Vector3(
      collider.size.x * .5,
      collider.size.y * .5,
      collider.size.z * .5,
    );
    const colliderCenter = new THREE.Vector3(
      collider.offset.x,
      collider.offset.y,
      collider.offset.z,
    ).add(offset);
    box.expandByPoint(colliderCenter.clone().sub(halfCollider));
    box.expandByPoint(colliderCenter.clone().add(halfCollider));
    for (const socket of [profile.sockets.weapon,profile.sockets.nitro]) {
      box.expandByPoint(new THREE.Vector3(socket.x,socket.y,socket.z).add(offset));
    }
    return box;
  }

  getPreviewBounds() {
    const box=this.getProfileBounds(this.rig,this.profile);
    if(this.comparisonRig&&this.comparisonMount.visible){
      box.union(this.getProfileBounds(
        this.comparisonRig,
        this.comparisonRig.profile,
        this.comparisonMount.position,
      ));
    }
    return box;
  }

  positionCameraForView(view, padding = 1.16) {
    const directions = {
      front: new THREE.Vector3(5.7, 3.2, -7.4),
      side: new THREE.Vector3(8.2, 2.6, 0),
      rear: new THREE.Vector3(-5.7, 3.2, 7.4),
      top: new THREE.Vector3(5.6, 8.2, -5.8),
    };
    const box = this.getPreviewBounds();
    if (box.isEmpty()) return false;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov * .5) * Math.max(this.camera.aspect, .2));
    const fittingFov = Math.min(verticalFov, horizontalFov);
    const distance = Math.max(
      5,
      sphere.radius / Math.max(Math.sin(fittingFov * .5), .08) * padding,
    );
    const direction = (directions[view] || directions.front).normalize();
    this.controls.target.copy(sphere.center);
    this.camera.position.copy(sphere.center).addScaledVector(direction, distance);
    this.camera.near = Math.max(.01, distance / 250);
    this.camera.far = Math.max(120, distance * 12);
    this.camera.updateProjectionMatrix();
    this.controls.update();
    return true;
  }

  setView(view) {
    this.positionCameraForView(view);
  }

  frameVehicle() {
    if (!this.positionCameraForView('front', 1.08)) {
      this.setView('front');
    }
  }

  resetFit() {
    const fresh = createVehicleProfile(this.profile.name);
    fresh.assets = { ...this.profile.assets };
    fresh.game={...this.profile.game};
    fresh.body.normalization = this.profile.body.normalization;
    fresh.wheels.normalization = this.profile.wheels.normalization;
    this.profile = fresh;
    this.rig.setProfile(this.profile);
    this.syncControls();
    this.applyProfile();
    this.setStatus('Ajustes restablecidos; los modelos cargados se conservaron.', 'ready');
  }

  async saveProfile() {
    if (!this.hasBody) {
      this.setStatus('Cargá la carrocería antes de guardar el perfil.', 'error');
      return;
    }
    const nameValidation=this.updateProfileNameValidation({announce:true});
    if(!nameValidation.valid)return;
    this.profile.name = nameValidation.id;
    this.profileName.value = this.profile.name;
    const profile = this.rig.snapshot();
    const contents = `${JSON.stringify(profile, null, 2)}\n`;
    const defaultName = `${profile.name}.vehicle.json`;
    try {
      if (this.callbacks.onSaveProfile) {
        const result = await this.callbacks.onSaveProfile({
          profile,
          bodyFile: this.assetFiles.body,
          wheelFile: this.assetFiles.wheel,
          contents,
          defaultName,
        });
        if (result?.handled !== false) {
          this.setStatus(result?.message || `Vehículo ${profile.name} guardado en el catálogo.`, 'success');
          return;
        }
      }
      if (window.toyboxDesktop?.saveVehicleProfile) {
        const result = await window.toyboxDesktop.saveVehicleProfile(defaultName, contents);
        if (result?.canceled) return;
        this.setStatus(`Perfil guardado en ${result.filePath}`, 'success');
        return;
      }
      const blob = new Blob([contents], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = defaultName;
      anchor.click();
      URL.revokeObjectURL(url);
      this.setStatus(`Perfil ${defaultName} descargado.`, 'success');
    } catch (error) {
      if(error.code==='catalog-id-exists'){
        this.applyProfileNameValidation(false,error.message);
        this.setStatus(error.message,'error');
      }else{
        this.setStatus(`No se pudo guardar el perfil: ${error.message}`, 'error');
      }
    }
  }

  setStatus(message, state = 'ready') {
    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  updateSaveAvailability(){
    this.saveButton.disabled=!this.hasBody||!this.profileNameIsAvailable;
  }

  applyProfileNameValidation(valid,message){
    this.profileNameIsAvailable=valid;
    this.profileNameValidationMessage=message;
    this.profileName.setAttribute('aria-invalid',String(!valid));
    this.profileName.setCustomValidity(valid?'':message);
    const label=this.profileName.closest('.calibrator-name');
    if(label)label.dataset.state=valid?'success':'error';
    if(this.profileNameMessage)this.profileNameMessage.textContent=message;
    this.updateSaveAvailability();
  }

  updateProfileNameValidation({announce=false}={}){
    const id=cleanProfileName(this.profileName.value);
    const result=this.callbacks.onValidateProfileName?.(id);
    const valid=result?.valid!==false;
    const message=result?.message||(
      valid
        ?'El ID está disponible y debe ser único dentro de todo el catálogo.'
        :`El ID técnico "${id}" ya existe. Elegí otro para no reemplazar el item anterior.`
    );
    this.applyProfileNameValidation(valid,message);
    if(announce&&!valid)this.setStatus(message,'error');
    return {id,valid,message};
  }

  resize() {
    if (!this.active) return;
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    if (!this.active) return;
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this.wheelSelectionToken++;
    for(const picker of this.equipmentPickers.values())picker.dispose();
    this.interiorEditor?.dispose();
    this.clearComparison();
    this.comparisonColliderGuide.geometry.dispose();
    this.comparisonColliderGuide.material.dispose();
    this.rig.dispose();
    this.dracoLoader.dispose();
    this.environmentTexture?.dispose();
    this.renderer.dispose();
    disposeLoadedScene(this.scene);
  }
}
